import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api.js";

interface Stats {
  periodLabel: string;
  total: number;
  pending: number;
  confirmed: number;
  active: number;
  completed: number;
  cancelled: number;
  noShow: number;
  noShowRate: number;
  completionRate: number;
  revenue: number;
  avgCheck: number;
  topServices: { name: string; count: number }[];
  sources: Record<string, number>;
}

const SOURCE_LABEL: Record<string, string> = {
  TELEGRAM: "Telegram", MAX: "MAX", VK: "VK",
  LANDING: "Сайт", DIRECT: "Напрямую", ADMIN_PANEL: "Админка",
};

function fmtMoney(n: number): string {
  return n.toLocaleString("ru-RU") + " ₽";
}

function pctDelta(curr: number, prev: number): { label: string; positive: boolean } | null {
  if (prev === 0 || curr === prev) return null;
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { label: `${pct > 0 ? "+" : ""}${pct}%`, positive: pct > 0 };
}

function monthKey(offset = 0): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-");
  return new Date(parseInt(y!), parseInt(m!) - 1, 1).toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
}

function ProgressBar({ value, max, color = "bg-primary" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function StatsPage({ token }: { token: string }) {
  const [month, setMonth] = useState(() => monthKey(0));

  const prevMonth = (() => {
    const [y, m] = month.split("-").map(Number) as [number, number];
    const prev = new Date(y, m - 2, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  })();

  const { data: curr, isLoading } = useQuery({
    queryKey: ["stats", month],
    queryFn: () => apiFetch<Stats>(token, `/api/appointments/stats?month=${month}`),
    staleTime: 60_000,
  });

  const { data: prev } = useQuery({
    queryKey: ["stats", prevMonth],
    queryFn: () => apiFetch<Stats>(token, `/api/appointments/stats?month=${prevMonth}`),
    staleTime: 60_000,
  });

  function shiftMonth(dir: number) {
    const [y, m] = month.split("-").map(Number) as [number, number];
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  const isCurrentMonth = month === monthKey(0);
  const s = curr;
  const p = prev;

  if (isLoading) {
    return <div className="p-4 text-center text-gray-400 py-10">Загрузка...</div>;
  }

  if (!s) {
    return (
      <div className="p-4 text-center py-14">
        <p className="text-4xl mb-3">📊</p>
        <p className="text-gray-500">Статистика появится после первых записей</p>
      </div>
    );
  }

  const revenueD = pctDelta(s.revenue, p?.revenue ?? 0);
  const totalD = pctDelta(s.total, p?.total ?? 0);

  return (
    <div className="mx-auto w-full max-w-2xl min-w-0 space-y-4 p-4">
      {/* Month selector */}
      <div className="flex min-w-0 items-center gap-2">
        <button onClick={() => shiftMonth(-1)} className="p-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-500">←</button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-sm font-semibold capitalize text-gray-800">{monthLabel(month)}</p>
          {!isCurrentMonth && (
            <button onClick={() => setMonth(monthKey(0))} className="text-xs text-primary hover:underline">↩ Текущий месяц</button>
          )}
        </div>
        <button onClick={() => shiftMonth(1)} disabled={isCurrentMonth} className="p-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 disabled:opacity-30">→</button>
      </div>

      {/* Revenue section */}
      <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Выручка</h3>
        <div className="grid min-w-0 grid-cols-2 gap-3">
          <div>
            <p className="text-2xl font-bold text-gray-800">{fmtMoney(s.revenue)}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-gray-400">{s.completed} завершено</p>
              {revenueD && (
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${revenueD.positive ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                  {revenueD.label}
                </span>
              )}
            </div>
            {p && p.revenue > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">Прошлый: {fmtMoney(p.revenue)}</p>
            )}
          </div>
          <div className="border-l border-gray-100 pl-3">
            <p className="text-2xl font-bold text-gray-800">{s.avgCheck > 0 ? fmtMoney(s.avgCheck) : "—"}</p>
            <p className="text-xs text-gray-400 mt-0.5">Средний чек</p>
          </div>
        </div>
      </div>

      {/* Appointments summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-3xl font-bold text-gray-800">{s.total}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-xs text-gray-400">записей всего</p>
            {totalD && (
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${totalD.positive ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                {totalD.label}
              </span>
            )}
          </div>
          {p && p.total > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">Прошлый: {p.total}</p>
          )}
        </div>
        <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-3xl font-bold text-yellow-500">{s.active}</p>
          <p className="text-xs text-gray-400 mt-0.5">активных</p>
        </div>
      </div>

      {/* Status breakdown */}
      {s.total > 0 && (
        <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">По статусам</h3>
          <div className="space-y-3">
            {[
              { label: "Завершено", count: s.completed, color: "bg-blue-400", badge: "bg-blue-50 text-blue-700", rate: `${s.completionRate}%` },
              { label: "Отменено", count: s.cancelled, color: "bg-gray-300", badge: "bg-gray-100 text-gray-600", rate: null },
              { label: "Не пришли", count: s.noShow, color: "bg-red-400", badge: "bg-red-50 text-red-600", rate: `${s.noShowRate}%` },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{row.label}</span>
                  <div className="flex items-center gap-2">
                    {row.rate && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row.badge}`}>{row.rate}</span>
                    )}
                    <span className="text-sm font-semibold text-gray-600 w-6 text-right">{row.count}</span>
                  </div>
                </div>
                <ProgressBar value={row.count} max={s.total} color={row.color} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top services */}
      {s.topServices.length > 0 && (
        <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Популярные услуги</h3>
          <div className="space-y-3">
            {s.topServices.map((svc, i) => (
              <div key={svc.name}>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right font-medium">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-700 truncate">{svc.name}</p>
                      <span className="text-sm font-semibold text-gray-600 ml-2 shrink-0">{svc.count}</span>
                    </div>
                    <ProgressBar value={svc.count} max={s.topServices[0]?.count ?? 1} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources */}
      {Object.keys(s.sources ?? {}).length > 0 && (
        <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Откуда записываются</h3>
          <div className="space-y-3">
            {Object.entries(s.sources ?? {})
              .sort(([, a], [, b]) => b - a)
              .map(([source, count]) => (
                <div key={source}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm text-gray-700">{SOURCE_LABEL[source] ?? source}</span>
                    <span className="text-sm font-semibold text-gray-600">{count}</span>
                  </div>
                  <ProgressBar value={count} max={s.total} color="bg-indigo-400" />
                </div>
              ))}
          </div>
        </div>
      )}

      {s.total === 0 && (
        <div className="text-center py-6">
          <p className="text-4xl mb-3">📊</p>
          <p className="text-gray-500">За этот месяц записей нет</p>
        </div>
      )}
    </div>
  );
}
